import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Footer from './components/common/Footer/Footer';
import Login from './components/auth/Login/Login';
import Signup from './components/auth/Signup/Signup';
import Verification from './components/auth/Verification/Verification';
import PasswordSetup from './components/auth/PasswordSetup/PasswordSetup';
import ChangePassword from './components/auth/ChangePassword/ChangePassword';
import HomeOwnerDashboard from './pages/HomeOwner/HomeOwnerDashboard';
import Chat from './pages/Chat/Chat';
import ForgotPassword from './components/auth/ForgotPassword/ForgotPassword';
import SendMessage from './components/support/contact_us/send_message/send_message';
import Profile from './pages/Profile/Profile';
import Settings from './pages/Settings/Settings';
import FloatingChat from './components/common/FloatingChat/FloatingChat';
import ComparisonRouter from './pages/HomeOwner/ComparisonRouter';
import HomeOwnerPricing from './pages/HomeOwner/HomeOwnerPricing';
import HomeOwnerSupport from './pages/HomeOwner/HomeOwnerSupport';
import HomeOwnerBilling from './pages/HomeOwner/HomeOwnerBilling';
import HomeOwnerPlanCheckout from './pages/HomeOwner/HomeOwnerPlanCheckout';
import CreateComparison from './pages/HomeOwner/CreateComparison';
import TermsOfService from './pages/Legal/TermsOfService';
import PrivacyPolicy from './pages/Legal/PrivacyPolicy';
import TermsModal from './components/common/TermsModal/TermsModal';
import TermsService from './services/termsService';
import GeoLocationService from './services/geoLocationService';
import GeoBlock from './components/common/GeoBlock/GeoBlock';
import GeoLocationTester from './components/common/GeoLocationTester/GeoLocationTester';


// Resource components removed due to missing files

// Admin imports removed due to missing files

// Import HomeOwner components
import HomeOwnerNavbar from './components/homeowner/HomeOwnerNavbar/HomeOwnerNavbar';
import HomeOwner from './pages/HomeOwner/HomeOwner';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return !!(token && user);
  });

  const [isAdmin, setIsAdmin] = useState(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.role === 'admin';
  });

  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsLoading, setTermsLoading] = useState(false);
  
  // Geolocation and compliance state
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(true);
  const [isBlocked, setIsBlocked] = useState(false);
  const [compliance, setCompliance] = useState(null);

  useEffect(() => {
    const handleStorageChange = () => {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      setIsAuthenticated(!!(token && user));
      setIsAdmin(user?.role === 'admin');
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Initialize geolocation and compliance checking
  useEffect(() => {
    const initializeGeolocation = async () => {
      try {
        setLocationLoading(true);
        const location = await GeoLocationService.getUserLocation();
        setUserLocation(location);

        // Check if user should be blocked
        if (GeoLocationService.isChinaBlocked(location)) {
          setIsBlocked(true);
          return;
        }

        // Set compliance requirements
        const complianceSettings = GeoLocationService.getRegionalCompliance(location);
        setCompliance(complianceSettings);

        // Store location in localStorage for other components
        localStorage.setItem('userLocation', JSON.stringify(location));
        localStorage.setItem('compliance', JSON.stringify(complianceSettings));

      } catch (error) {
        console.error('Geolocation initialization failed:', error);
        // Fail gracefully - assume no restrictions
        setIsBlocked(false);
        setCompliance({
          showCookieConsent: false,
          showDataProtectionNotices: false,
          showAIDisclaimers: false,
          requiresOptOut: false,
          blockedRegion: false
        });
      } finally {
        setLocationLoading(false);
      }
    };

    initializeGeolocation();
  }, []);

  // Check if user needs to accept terms when they become authenticated
  useEffect(() => {
    const checkTermsAcceptance = async () => {
      if (isAuthenticated) {
        try {
          const needsAcceptance = await TermsService.checkIfUserNeedsToAcceptTerms();
          setShowTermsModal(needsAcceptance);
        } catch (error) {
          console.error('Error checking terms acceptance:', error);
          // Don't show modal if there's an error
        }
      }
    };

    checkTermsAcceptance();
  }, [isAuthenticated]);

  // Protected Route component with role check
  const ProtectedRoute = ({ children, requireAdmin }) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" />;
    }
    if (requireAdmin && !isAdmin) {
      return <Navigate to="/dashboard" />;
    }
    return children;
  };

  // Check if the current path is a homeowner path
  const isHomeOwnerPath = (pathname) => {
    return pathname.startsWith('/homeowner') || pathname === '/';
  };

  // Terms acceptance handlers
  const handleAcceptTerms = async () => {
    setTermsLoading(true);
    try {
      await TermsService.acceptTerms('1.0');
      setShowTermsModal(false);
      // Show success message or refresh user data if needed
    } catch (error) {
      console.error('Error accepting terms:', error);
      alert('Failed to accept terms. Please try again.');
    } finally {
      setTermsLoading(false);
    }
  };

  const handleDeclineTerms = () => {
    // If user declines terms, log them out
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setIsAdmin(false);
    setShowTermsModal(false);
    // Redirect to home or login page
    window.location.href = '/';
  };

  // Get the appropriate navbar based on the path and user role
  const getNavbar = () => {
    // Get user if authenticated
    const user = isAuthenticated ? JSON.parse(localStorage.getItem('user') || '{}') : null;
    
    if (isAuthenticated) {
      // Show navbar based on user role, regardless of current path
      if (user?.role === 'homeowner') {
        return <HomeOwnerNavbar />;
      } else {
        // For all other roles (like 'user'), show homeowner navbar for now
        return <HomeOwnerNavbar />;
      }
    } else if (isHomeOwnerPath(window.location.pathname)) {
      // Not logged in but on homeowner path
      return <HomeOwnerNavbar />;
    } else {
      return <HomeOwnerNavbar />;
    }
  };

  // Handle retry for geo-blocked users
  const handleGeoRetry = () => {
    setLocationLoading(true);
    setIsBlocked(false);
    // Re-initialize geolocation
    window.location.reload();
  };

  // Show loading screen while determining location
  if (locationLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{ fontSize: '2rem' }}>🌍</div>
        <div>Initializing Trakvu...</div>
      </div>
    );
  }

  // Show geo-block screen for blocked regions
  if (isBlocked && userLocation) {
    return <GeoBlock location={userLocation} onRetry={handleGeoRetry} />;
  }

  return (
    <Router>
      <div className="App">
        {getNavbar()}
        <Routes>
          <Route path="/" element={<HomeOwner />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/verify" element={<Verification />} />
          <Route path="/password-setup" element={<PasswordSetup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route
            path="/homeowner/dashboard"
            element={
              <ProtectedRoute>
                <HomeOwnerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/homeowner/comparisons/:id"
            element={<ComparisonRouter />}
          />
          <Route
            path="/chat"
            element={
              <ProtectedRoute>
                <Chat />
              </ProtectedRoute>
            }
          />
          <Route path="/support/contact" element={<SendMessage />} />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/change-password" 
            element={
              <ProtectedRoute>
                <ChangePassword />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          {/* Resources Routes removed due to missing files */}

          {/* HomeOwner routes */}
          <Route path="/homeowner" element={<HomeOwner />} />
          <Route path="/homeowner/solutions" element={<div>Home Owner Solutions</div>} />
          <Route path="/homeowner/pricing" element={<HomeOwnerPricing />} />
          <Route path="/homeowner/support" element={<HomeOwnerSupport />} />
          <Route path="/homeowner/contact" element={<HomeOwnerSupport />} />
          <Route path="/homeowner/knowledge-base" element={<div>Knowledge Base Coming Soon</div>} />
          <Route path="/homeowner/tutorials" element={<div>Video Tutorials Coming Soon</div>} />
          <Route path="/homeowner/find-contractors" element={<div>Find Contractors</div>} />
          <Route path="/homeowner/how-it-works" element={<div>How It Works</div>} />
          <Route path="/homeowner/projects/:type" element={<div>Project Details</div>} />
          <Route path="/homeowner/login" element={<Login />} />
          <Route path="/homeowner/signup" element={<Signup />} />
          <Route path="/homeowner/forgot-password" element={<ForgotPassword />} />
          <Route path="/homeowner/profile" element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } />
          <Route path="/homeowner/settings" element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          } />
          <Route path="/homeowner/billing" element={
            <ProtectedRoute>
              <HomeOwnerBilling />
            </ProtectedRoute>
          } />
          <Route path="/homeowner/checkout" element={
            <ProtectedRoute>
              <HomeOwnerPlanCheckout />
            </ProtectedRoute>
          } />
          <Route path="/homeowner/create-comparison" element={<CreateComparison />} />
          
          {/* Legal Routes */}
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
        </Routes>
        <Footer />
        {isAuthenticated && !isAdmin && !isHomeOwnerPath(window.location.pathname) && <FloatingChat />}
        
        {/* Geolocation Testing Tool (Development Only) */}
        <GeoLocationTester />
        
        {/* Terms of Service Modal */}
        <TermsModal
          isOpen={showTermsModal}
          onAccept={handleAcceptTerms}
          onDecline={handleDeclineTerms}
          loading={termsLoading}
        />
      </div>
    </Router>
  );
}

export default App;
